import React, { FC, useEffect, useRef, useState } from 'react';
import DefaultPageLayout from '../../common/DefaultPageLayout';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../store';
import {
  countriesSelector,
  countryOptions,
  getSelectedCountryCodes,
} from '../../store/selectData/selectors';
import { selectCountryCodes } from '../../store/selectData/actionWrappers';
import ApprovalModal from '../ApprovalModal';
import {
  selectApprovalLoading,
  selectApprovalProjects,
} from '../../store/approval/selectors';
import {
  fetchApprovalProjects,
  fetchApprovalProject,
  setSelectedProjectId,
  captureRevisedBudget,
} from '../../store/approval/actionWrappers';
import ArchiveProjectPrompt from '../../components/ArchiveProjectPrompt';
import {
  archiveProjects,
  clearSolutions,
  getProjectDetails,
} from '../../store/projects/actionWrapper';
import EditProjectModals from '../../components/EditProjectModals';
import { ESTABLISHMENT_TYPE } from '../../components/ProjectTypeSelect/utils';
import { selectProjectToEditWithFormattedInvestments } from '../../store/projects/selectors';
import { TableProject } from '../../models/project';
import { projectsActions } from '../../store/projects/slice';
import { useAnalytics } from '../../hooks/useAnalytics';
import ApprovalPageComponent from '../../components/ApprovalPageComponent';

const ApprovalPage: FC = () => {
  const dispatch = useDispatch();
  const { actionStart, actionEnd, actionDiscarded } =
    useAnalytics('projectUpdated');
  const countryList = useTypedSelector(countriesSelector);
  const countriesOptions = useTypedSelector(countryOptions);
  const formattedProject = useTypedSelector(
    selectProjectToEditWithFormattedInvestments,
  );
  const [selectedCrp, setSelectedCrp] = useState<string[]>(['noCrp']);
  const reduxSelectedCountries = useTypedSelector(getSelectedCountryCodes);
  const selectedCountries = reduxSelectedCountries.map((c) => c.countryId);
  const [editModalType, setEditModalType] = useState<ESTABLISHMENT_TYPE | null>(
    null,
  );
  const [openWithDirective, setOpenWithDirective] = useState(false);

  const onSelectedCountriesChange = (countryIds: string[]) => {
    dispatch(
      selectCountryCodes(
        countryIds
          .map((id) => countryList.find((c) => c.countryId === id))
          .filter((c) => c !== undefined),
      ),
    );
    if (selectedCrp[0] === 'noCrp') {
      if (countryIds.length !== 0) {
        dispatch(fetchApprovalProjects(countryIds));
      }
    }
  };
  const projects = useTypedSelector(selectApprovalProjects);
  const loading = useTypedSelector(selectApprovalLoading);
  const [isArchiveProjectModal, setIsArchiveProjectModal] = useState(false);
  const [tableSelection, setTableSelection] = useState<string[]>([]);
  const [approvalModalVisible, setApprovalModalVisible] =
    useState<boolean>(false);

  // const deleteProjectHandler = () => {
  //   console.log('deleteProjectHandler', tableSelection);
  // };
  const editProjectHandler = (projectId) => {
    const project = projects.find((el) => el.id === projectId);
    setEditModalType(project?.establishmentType as ESTABLISHMENT_TYPE);
    setOpenWithDirective(false);
    actionStart();
    dispatch(getProjectDetails({ id: projectId }));
  };

  const directiveClickHandler = (projectId: string) => {
    const project = projects.find((el) => el.referenceId === projectId);
    setEditModalType(project?.establishmentType as ESTABLISHMENT_TYPE);
    setOpenWithDirective(true);
    actionStart();
    dispatch(getProjectDetails({ id: projectId, refId: true }));
  };
  const archiveProjectHandler = () => {
    setIsArchiveProjectModal(true);
  };
  const handleCloseArchiveModal = () => {
    setIsArchiveProjectModal(false);
    dispatch(clearSolutions());
  };

  const handleCancelArchiveProjects = () => {
    setIsArchiveProjectModal(false);
  };
  const handleAcceptArchiveProjects = async () => {
    await dispatch(archiveProjects(tableSelection));

    dispatch(fetchApprovalProjects(selectedCrp));

    setTableSelection([]);
    setIsArchiveProjectModal(false);
  };
  const closeEditModal = () => {
    if (editModalType) {
      setEditModalType(null);
      setOpenWithDirective(false);
      actionDiscarded();
      dispatch(projectsActions.clearProject());
      dispatch(clearSolutions());
    }
  };

  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (initialFetchDone.current) return;
    if (selectedCountries.length > 0 && !loading) {
      initialFetchDone.current = true;
      dispatch(fetchApprovalProjects(selectedCountries));
    }
  }, [selectedCountries]);

  const openApprovalModalHandler = () => {
    setApprovalModalVisible(true);
  };
  const closeApprovalModalHandler = () => {
    setApprovalModalVisible(false);
    dispatch(setSelectedProjectId(null));
    setTableSelection([]);
  };
  const approveProjectHandler = () => {
    if (tableSelection.length === 1) {
      dispatch(setSelectedProjectId(tableSelection[0]));
    }
    openApprovalModalHandler();
  };

  const captureRevisedBudgetHandler = (payload: {
    projectIds: string[];
    fiscalYear: string;
  }) => {
    dispatch(captureRevisedBudget(payload));
  };

  const onCRPChange = (newCrps: string[]) => {
    setSelectedCrp(newCrps);
    if (
      newCrps.length === 1 &&
      newCrps[0] === 'noCrp' &&
      selectedCountries.length > 0
    ) {
      dispatch(fetchApprovalProjects(selectedCountries));
      return;
    }
    dispatch(fetchApprovalProjects(newCrps));
  };

  return (
    <>
      <DefaultPageLayout
        sidebar={() => <></>}
        mainPart={() => (
          <Box pr={2} height="100%" mt={4}>
            <ApprovalPageComponent
              selectedCRPValues={selectedCrp}
              onSelectedCRPValuesChange={onCRPChange}
              projects={projects}
              countriesOptions={countriesOptions}
              onSelectedCountryChange={onSelectedCountriesChange}
              selectedCountry={selectedCountries}
              setTableSelection={setTableSelection}
              tableSelection={tableSelection}
              approveProject={approveProjectHandler}
              captureRevisedBudget={captureRevisedBudgetHandler}
              onRowClick={editProjectHandler}
              archiveProject={archiveProjectHandler}
              loading={loading}
              directiveClickedEvent={directiveClickHandler}
            />
          </Box>
        )}
      />
      <ArchiveProjectPrompt
        isVisible={isArchiveProjectModal}
        onClose={handleCloseArchiveModal}
        onCancel={handleCancelArchiveProjects}
        onAccept={handleAcceptArchiveProjects}
      />
      <ApprovalModal
        visible={approvalModalVisible}
        selectedProjects={tableSelection}
        approvalProjectFetchValues={
          selectedCrp.length === 1 && selectedCrp[0] === 'noCrp'
            ? selectedCountries
            : selectedCrp
        }
        onCloseHandler={closeApprovalModalHandler}
      />
      <EditProjectModals
        establishmentType={editModalType}
        closeHandler={closeEditModal}
        project={formattedProject}
        projectsList={projects as unknown as TableProject[]}
        countries={countryList}
        disableRestrictions
        additionalAction={
          formattedProject?.id
            ? fetchApprovalProject(formattedProject.id)
            : undefined
        }
        submitTrigger={() =>
          actionEnd({
            eventSubType: 'Modal',
            context: { ProjectType: editModalType },
          })
        }
        directiveOpen={openWithDirective}
      />
    </>
  );
};

export default ApprovalPage;
